import FormContainer from "@client/content/controls/Form/container";
import { getRootContainerProps } from "@client/content/lib/helper";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { type ControlFormField, type FormFieldInfo, type FormHandles } from "@client/controls/Form/component";
import Checkbox from "@client/controls/Inputs/Checkbox/component";
import PhoneInput from "@client/controls/Inputs/PhoneInput/component";
import Select, { DynamicSelect } from "@client/controls/Inputs/Select/component";
import AddressInput from "@client/controls/Inputs/TextInput/Address/component";
import withIsVisibleHOC, { type WithIsVisibleHOCProps } from "@client/hocs/withIsVisible";
import { toMap } from "@client/lib/array";
import { usePromise } from "@client/lib/hooks";
import { validatePhone } from "@common/components/Form/Field/lib";
import { getDialCode } from "@common/controls/Inputs/PhoneInput/lib";
import { type OptionItem } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { mapFromBillingAddress, mapFromShippingAddress } from "@common/lib/address";
import getOptions from "@common/lib/options";
import { isNull } from "lodash";
import * as React from "react";
import { withOnSubmit } from "../../hooks";
import { type Checkout } from "../../types";
import { getMapOutput } from "./lib";

export type MapProps = RootContainerProps &
  WithIsVisibleHOCProps & {
    addressType: "billing" | "shipping";
    apiInfo: ModuleApiInfo;
    excludeFields: Checkout.Api.Field[];
    isSameAsShipping: boolean | null;
    items: Array<"FirstName" | "LastName" | "Address" | "Phone" | "Company">; // maps to either billingItems or shippingItems setting
    order: Checkout.Order;
    requiresShipping: boolean;
    shouldStackFields: boolean;
  };
export interface ActionProps {
  onChange: (name: string, value: any) => void;
  onRegionsLoaded: (options: OptionItem<string>[]) => void;
}
export type Props = ActionProps & MapProps;

export default withIsVisibleHOC((props: Props) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const [data, setData] = React.useState<Partial<Checkout.View.Address>>({});

  React.useEffect(
    () =>
      setData({
        ...(props.addressType === "billing" ? mapFromBillingAddress(props.order) : mapFromShippingAddress(props.order)),
        isSameAsShipping: props.isSameAsShipping
      }),
    [props.addressType, props.isSameAsShipping, props.order]
  );

  const ref = withOnSubmit<FormHandles>(getMapOutput(props.addressType));

  const onChange = React.useCallback(
    async (name: keyof Checkout.View.UserAddress, value: any) => {
      props.onChange(name, value);

      if (name === "countryCode") {
        setData({ ...data, [name]: value, subdivisionCode: undefined });
        props.onChange("subdivisionCode", undefined);
      } else {
        setData({ ...data, [name]: value });
      }
    },
    [data, setData, props.onChange]
  );

  const [regions, isLoadingRegions] = usePromise({
    fn: options.regions,
    args: [data.countryCode],
    shouldLoad: !!data.countryCode
  });

  React.useEffect(() => {
    if (!isLoadingRegions && regions) {
      props.onRegionsLoaded(regions);
    }
  }, [regions, props.onRegionsLoaded]);

  const onChangeTrigger = React.useMemo(
    () => (props.addressType === "shipping" ? "change" : "blur"),
    [props.addressType]
  );

  const fields = React.useMemo((): FormFieldInfo[] => {
    const fields: FormFieldInfo[] = [];

    if (!isNull(props.isSameAsShipping) && props.requiresShipping) {
      fields.push({
        name: "isSameAsShipping",
        Component: Checkbox,
        componentOpts: { label: i18n.sameAsShippingAddress },
        defaultValue: data.isSameAsShipping,
        qa: "Same as shipping"
      });
    }

    if (!props.isSameAsShipping || !props.requiresShipping) {
      if (props.items.includes("FirstName")) {
        fields.push({
          name: "firstName",
          label: i18n.firstName,
          isRequired: true,
          Component: AddressInput,
          componentOpts: { onChangeTrigger },
          colSpan: props.items.includes("LastName") ? 6 : 12,
          defaultValue: data.firstName,
          qa: "First name"
        });
      }

      if (props.items.includes("LastName")) {
        fields.push({
          name: "lastName",
          label: i18n.lastName,
          isRequired: true,
          Component: AddressInput,
          componentOpts: { onChangeTrigger },
          colSpan: props.items.includes("FirstName") ? 6 : 12,
          defaultValue: data.lastName,
          qa: "Last name"
        });
      }

      fields.push({
        name: "countryCode",
        label: i18n.country,
        isRequired: true,
        Component: DynamicSelect,
        componentOpts: { onChangeTrigger, options: () => options.countries(), sort: "asc" },
        colSpan: 6,
        defaultValue: data.countryCode,
        qa: "Country"
      });

      fields.push({
        name: "subdivisionCode",
        isRequired: !!regions?.length,
        label: i18n.region,
        Component: Select,
        componentOpts: {
          onChangeTrigger,
          options: regions ?? [],
          sort: "asc"
        },
        colSpan: 6,
        defaultValue: data.subdivisionCode,
        dependencies: ["countryCode"],
        qa: "Region"
      });

      if (!props.excludeFields.includes("City")) {
        fields.push({
          name: "city",
          label: i18n.city,
          isRequired: true,
          Component: AddressInput,
          colSpan: props.excludeFields.includes("PostalCode") ? 12 : 6,
          componentOpts: { onChangeTrigger },
          defaultValue: data.city,
          qa: "City"
        });
      }

      if (!props.excludeFields.includes("PostalCode")) {
        fields.push({
          name: "postalCode",
          label: i18n.postalCode,
          isRequired: true,
          Component: AddressInput,
          componentOpts: { onChangeTrigger },
          colSpan: props.excludeFields.includes("City") ? 12 : 6,
          defaultValue: data.postalCode,
          qa: "Postal code"
        });
      }

      if (!props.excludeFields.includes("Street")) {
        fields.push({
          name: "street",
          label: i18n.streetAddress,
          isRequired: true,
          Component: AddressInput,
          componentOpts: { onChangeTrigger },
          colSpan: props.excludeFields.includes("Unit") ? 12 : 6,
          defaultValue: data.street,
          qa: "Street"
        });
      }

      if (!props.excludeFields.includes("Unit")) {
        fields.push({
          name: "unit",
          label: i18n.unitAptSuite,
          Component: AddressInput,
          componentOpts: { onChangeTrigger },
          colSpan: props.excludeFields.includes("Street") ? 12 : 6,
          defaultValue: data.unit,
          qa: "Unit"
        });
      }

      if (props.items.includes("Phone")) {
        fields.push({
          name: "telephone",
          label: i18n.phone,
          isRequired: true,
          Component: PhoneInput,
          defaultValue: data.telephone ?? (data.countryCode ? getDialCode(data.countryCode) : ""),
          componentAcceptsLabel: true,
          componentOpts: { onChangeTrigger: "blur" },
          validation: validatePhone,
          qa: "Phone"
        });
      }

      if (props.items.includes("Company")) {
        fields.push({
          name: "company",
          label: i18n.company,
          Component: AddressInput,
          colSpan: data.company ? 6 : 12,
          defaultValue: data.company,
          qa: "Company"
        });

        if (data.company) {
          fields.push({
            name: "companyTaxNumber",
            label: i18n.taxNumber,
            Component: AddressInput,
            componentOpts: { onChangeTrigger },
            colSpan: 6,
            defaultValue: data.companyTaxNumber,
            qa: "Tax number"
          });
        }
      }
    }

    if (props.shouldStackFields) {
      return fields.map((x) => ({ ...x, colCpan: 12 }));
    }

    return fields;
  }, [
    data,
    regions,
    props.apiInfo,
    props.excludeFields,
    i18n,
    props.isSameAsShipping,
    props.items,
    props.shouldStackFields,
    props.requiresShipping
  ]);

  const values = React.useMemo(
    () =>
      toMap(
        fields as ControlFormField[],
        (x) => x.name,
        (x) => x.defaultValue
      ),
    [fields]
  );

  return (
    <div className="address_information">
      <h4>{props.addressType === "shipping" ? i18n.shippingInformation : i18n.billingInformation}</h4>

      <FormContainer {...getRootContainerProps(props)} ref={ref} fields={fields} onChange={onChange} values={values} />
    </div>
  );
});
